import { useEffect, useState } from "react";

/* ═══ Shared design tokens for all report screens ═══ */
/**
 * Shown behind the info icon on report titles and section headers that display
 * money derived from item/order values. One string so the wording cannot drift
 * between Snapshot, Sales and Product.
 */
export const REPORT_BASIS_NOTE =
    'Figures shown in this report reflect net values (excluding applicable taxes and all discounts).';

export const T = {
    bg: "#F5F6FA",
    surface: "#FFFFFF",
    card: "#FFFFFF",
    hover: "#F0F1F5",
    accent: "#D23A3B",
    accentSoft: "rgba(210,58,59,0.08)",
    accentGlow: "rgba(210,58,59,0.15)",
    green: "#16A34A",
    greenSoft: "rgba(22,163,74,0.08)",
    red: "#DC2626",
    redSoft: "rgba(220,38,38,0.08)",
    orange: "#D97706",
    orangeSoft: "rgba(217,119,6,0.08)",
    blue: "#3B82F6",
    blueSoft: "rgba(59,130,246,0.08)",
    blueDark: "#2563EB",
    blueDarkSoft: "rgba(37,99,235,0.08)",
    purple: "#7C3AED",
    purpleSoft: "rgba(124,58,237,0.08)",
    teal: "#0D9488",
    tealSoft: "rgba(13,148,136,0.08)",
    t1: "#1A1D2E",
    t2: "#475569",
    t3: "#64748B",
    /* Order Log ink scale (ORD-1.2 / ORD-1.4): DARK = real value,
       MID = reduction / uncollected value, LIGHT = value unavailable. */
    inkDark: "#1A1D2E",
    inkMid: "#5A6178",
    inkLight: "#9CA3B8",
    cardBorder: "#E2E5EC",
    divider: "#E8EAF0",
    shadow: "0 1px 3px rgba(0,0,0,0.06)",
    cardShadow: "0 1px 4px rgba(0,0,0,0.05)",
    sidebarShadow: "2px 0 8px rgba(0,0,0,0.03)",
    topbarShadow: "0 1px 3px rgba(0,0,0,0.04)",
    /** Floating layers - tooltips, dropdown lists. */
    popoverShadow: "0 6px 20px rgba(0,0,0,0.14)",
    font: "'DM Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
};

/* ═══ Responsive hook ═══ */
export const useIsDesktop = () => {
    const query = "(min-width: 900px)";
    const getMatches = () => typeof window !== "undefined" && window.matchMedia(query).matches;
    const [isDesktop, setIsDesktop] = useState(getMatches);
    useEffect(() => {
        if (typeof window === "undefined") return undefined;
        const media = window.matchMedia(query);
        const onChange = (e: MediaQueryListEvent) => setIsDesktop(e.matches);
        setIsDesktop(media.matches);
        media.addEventListener("change", onChange);
        return () => media.removeEventListener("change", onChange);
    }, []);
    return isDesktop;
};

/* ═══ Shimmer skeleton ═══ */
interface BoneProps {
    w?: number | string | null;
    h?: number;
    r?: number;
    mb?: number;
    style?: React.CSSProperties;
}

export const Bone = ({ w, h = 14, r = 6, mb = 0, style: extra }: BoneProps) => (
    <div style={{
        width: w ?? "100%", height: h, borderRadius: r, marginBottom: mb,
        background: "linear-gradient(90deg, #e8eaf0 25%, #f3f4f8 37%, #e8eaf0 63%)",
        backgroundSize: "800px 100%", animation: "shimmer 1.4s ease infinite",
        ...extra,
    }} />
);
